import React, { useEffect, useReducer, useRef } from 'react';
import axios from 'axios';
import {
  Button, ButtonToolbar, Form, FormInstance, Loader, SelectPicker,
} from 'rsuite';
import handleError from '../../../../api/apiErrorHandle';
import { CreatedPartner } from '../../../../api/partner/partnerApiTypes';
import PartnerCodeInput from './PartnerCodeInput';
import { checkCodeAvailability, createNewPartner, fetchNextPartnerCode } from './partnerCreateService';
import {
  CREATABLE_INTEGRATIONS, Edit, canSubmit, initialState, isCodeVerified, needsCodeCheck, reducer,
} from './partnerCreateFormState';
import validationModel, { getPartnerCodeErrorMessage } from './partnerCreateValidation';

const INTEGRATIONS = CREATABLE_INTEGRATIONS.map(integration => ({
  value: integration,
  label: integration,
}));

interface Props {
  onCancel: () => void;
  onCreated: (createdPartner: CreatedPartner) => void;
  onCreatingChange: (isCreating: boolean) => void;
}

export default function PartnerCreateForm({
  onCancel, onCreated, onCreatingChange,
}: Props): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const formRef = useRef<FormInstance>(null);
  const codeCheckControllerRef = useRef<AbortController | null>(null);

  async function suggestNextCode(signal: AbortSignal) {
    try {
      const code = await fetchNextPartnerCode(signal);
      dispatch({ type: 'code/suggested', payload: code });
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }

      handleError(error);
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  useEffect(() => {
    const controller = new AbortController();
    suggestNextCode(controller.signal).then();

    return () => {
      controller.abort();
      codeCheckControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    onCreatingChange(state.status === 'creating');
  }, [state.status]);

  async function checkCode(code: string): Promise<boolean> {
    codeCheckControllerRef.current?.abort();
    const controller = new AbortController();
    codeCheckControllerRef.current = controller;

    dispatch({ type: 'code/checking' });

    try {
      const available = await checkCodeAvailability(code, controller.signal);
      dispatch({ type: 'code/checked', payload: { code, available } });

      return available;
    } catch (error) {
      if (!axios.isCancel(error)) {
        handleError(error);
        dispatch({ type: 'code/reset' });
      }

      return false;
    }
  }

  function handleChange(edit: Edit) {
    dispatch({ type: 'edit/set', payload: edit });
  }

  function handleCodeBlur() {
    const code = state.edit.code.trim();

    if (!code || !needsCodeCheck(state)) {
      return;
    }

    checkCode(code).then();
  }

  async function submitPartner() {
    dispatch({ type: 'status/set', payload: 'creating' });

    try {
      const edit: Edit = {
        ...state.edit,
        code: state.edit.code.trim(),
        partnerName: state.edit.partnerName.trim(),
      };

      const isCodeFree = isCodeVerified(state) || await checkCode(edit.code);

      if (!isCodeFree) {
        dispatch({ type: 'status/set', payload: 'ready' });
        return;
      }

      const createdPartner = await createNewPartner(edit);
      onCreated(createdPartner);
    } catch (error) {
      handleError(error);
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  function handleSubmit() {
    if (!formRef.current?.check()) {
      return;
    }

    submitPartner().then();
  }

  if (state.status === 'loading') {
    return <Loader content="Loading partner code..." />;
  }

  return (
    <Form
      ref={formRef}
      fluid
      formValue={state.edit}
      onChange={(edit) => handleChange(edit as Edit)}
      model={validationModel}
      layout="vertical"
    >
      <Form.Group controlId="code">
        <Form.ControlLabel>Partner code</Form.ControlLabel>
        <Form.Control
          name="code"
          type="text"
          accepter={PartnerCodeInput}
          loading={state.codeCheck === 'checking'}
          onBlur={() => handleCodeBlur()}
          errorMessage={getPartnerCodeErrorMessage(state)}
        />
      </Form.Group>
      <Form.Group controlId="partnerName">
        <Form.ControlLabel>Partner name</Form.ControlLabel>
        <Form.Control name="partnerName" type="text" />
      </Form.Group>
      <Form.Group controlId="integration">
        <Form.ControlLabel>Integration</Form.ControlLabel>
        <Form.Control
          name="integration"
          accepter={SelectPicker}
          data={INTEGRATIONS}
          cleanable={false}
          searchable={false}
          block
        />
      </Form.Group>

      <ButtonToolbar>
        <Button
          appearance="primary"
          onClick={() => handleSubmit()}
          loading={state.status === 'creating'}
          disabled={!canSubmit(state)}
        >
          Submit
        </Button>
        <Button
          appearance="ghost"
          onClick={() => onCancel()}
          disabled={state.status === 'creating'}
        >
          Cancel
        </Button>
      </ButtonToolbar>
    </Form>
  );
}
